export type CoverRevealPhase = 'cover' | 'release' | 'stage';

export type CoverRevealTiming = {
  phase: CoverRevealPhase;
  startFraction: number;
  endFraction: number;
};

type GetCoverRevealProgressArgs = {
  stageProgress: number;
  holdRatio: number;
  coverRatio: number;
  releaseRatio: number;
  coverRevealTiming: CoverRevealTiming;
};

const EPSILON = 0.0001;

const clamp = (min: number, max: number, value: number) =>
  Math.min(max, Math.max(min, value));

export const getViewportRevealTriggerPosition = (
  viewportHeight: number,
  viewportRatio: number
) => {
  const safeViewportHeight = Math.max(1, viewportHeight);
  const safeViewportRatio = clamp(0.05, 0.95, viewportRatio);

  return `top ${Math.round(safeViewportHeight * safeViewportRatio)}px`;
};

export const DEFAULT_COVER_REVEAL_TIMING: CoverRevealTiming = {
  phase: 'cover',
  startFraction: 0,
  endFraction: 1,
};

export const getCoverRevealProgress = ({
  stageProgress,
  holdRatio,
  coverRatio,
  releaseRatio,
  coverRevealTiming,
}: GetCoverRevealProgressArgs): number => {
  const totalRatio = holdRatio + coverRatio + releaseRatio;
  const clampedStageProgress = clamp(0, 1, stageProgress);
  const startFraction = clamp(0, 1, coverRevealTiming.startFraction);
  const endFraction = clamp(startFraction, 1, coverRevealTiming.endFraction);
  const phaseOffset =
    coverRevealTiming.phase === 'release'
      ? holdRatio + coverRatio
      : coverRevealTiming.phase === 'cover'
        ? holdRatio
        : 0;
  const phaseDuration =
    coverRevealTiming.phase === 'release'
      ? releaseRatio
      : coverRevealTiming.phase === 'cover'
        ? coverRatio
        : totalRatio;
  const phaseProgress =
    coverRevealTiming.phase === 'stage'
      ? clampedStageProgress
      : (clampedStageProgress * totalRatio - phaseOffset) /
        Math.max(phaseDuration, EPSILON);
  const normalizedProgress =
    (phaseProgress - startFraction) / Math.max(endFraction - startFraction, EPSILON);

  return clamp(0, 1, normalizedProgress);
};
